import React, { useState } from "react";
import { useTranslation } from "react-i18next";

const ProMatchSimulationSection = ({ statistics }) => {
  const { t } = useTranslation();

  if (!statistics || !statistics.pro_match_results) {
    return (
      <div className="text-white text-sm bg-secondary/30 backdrop-blur-sm rounded-lg p-4 sm:p-6 shadow-md border border-primary/10">
        <p className="mb-2">{t("statistics.proMatch.noData")}</p>
      </div>
    );
  }

  const proMatchResults = statistics.pro_match_results;
  const [selectedPlayer, setSelectedPlayer] = useState("Michael Smith");
  const [matchType, setMatchType] = useState("BO3");
  const [startMode, setStartMode] = useState("AlwaysStart");
  const [showResults, setShowResults] = useState(false);

  const [confirmedPlayer, setConfirmedPlayer] = useState("Michael Smith");
  const [confirmedMatchType, setConfirmedMatchType] = useState("BO3");
  const [confirmedStartMode, setConfirmedStartMode] = useState("AlwaysStart");

  const [isSimulating, setIsSimulating] = useState(false);

  const key = `${confirmedMatchType}_bulloff_${
    confirmedStartMode === "Bulloff" ? "True" : "False"
  }`;
  const data = proMatchResults?.[confirmedPlayer]?.[key];

  const handlePlay = () => {
    const delay = 3000 + Math.floor(Math.random() * 300);
    setIsSimulating(true);
    setTimeout(() => {
      setConfirmedPlayer(selectedPlayer);
      setConfirmedMatchType(matchType);
      setConfirmedStartMode(startMode);
      setIsSimulating(false);
      setShowResults(true);
    }, delay);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
      <div className="bg-secondary/30 backdrop-blur-sm rounded-2xl p-6 sm:p-8 shadow-lg border border-primary/10 hover:border-primary/20 transition-all duration-300 transform hover:scale-[1.01]">
        <h4 className="text-test text-sm font-semibold mb-6 tracking-wide uppercase flex items-center">
          <span className="w-8 h-8 rounded-full bg-test/20 flex items-center justify-center mr-3">
            <svg
              className="w-4 h-4 text-test"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </span>
          {t("statistics.proMatch.configure")}
        </h4>

        <div className="space-y-5">
          <div className="group">
            <label className="block text-white/90 text-xs font-medium mb-2 tracking-wide group-hover:text-test transition-colors duration-200">
              {t("statistics.proMatch.player")}
            </label>
            <select
              className="w-full p-3 rounded-xl bg-black/40 text-white text-sm border border-primary/20 focus:border-test focus:ring-2 focus:ring-test/20 outline-none transition-all duration-200 hover:bg-black/50"
              value={selectedPlayer}
              onChange={(e) => setSelectedPlayer(e.target.value)}
            >
              {Object.keys(proMatchResults).map((player) => (
                <option key={player} value={player}>
                  {player}
                </option>
              ))}
            </select>
          </div>

          <div className="group">
            <label className="block text-white/90 text-xs font-medium mb-2 tracking-wide group-hover:text-test transition-colors duration-200">
              {t("statistics.proMatch.matchType")}
            </label>
            <select
              className="w-full p-3 rounded-xl bg-black/40 text-white text-sm border border-primary/20 focus:border-test focus:ring-2 focus:ring-test/20 outline-none transition-all duration-200 hover:bg-black/50"
              value={matchType}
              onChange={(e) => setMatchType(e.target.value)}
            >
              <option value="BO1">{t("statistics.proMatch.bo1")}</option>
              <option value="BO3">{t("statistics.proMatch.bo3")}</option>
            </select>
          </div>

          <div className="group">
            <label className="block text-white/90 text-xs font-medium mb-2 tracking-wide group-hover:text-test transition-colors duration-200">
              {t("statistics.proMatch.startMode")}
            </label>
            <select
              className="w-full p-3 rounded-xl bg-black/40 text-white text-sm border border-primary/20 focus:border-test focus:ring-2 focus:ring-test/20 outline-none transition-all duration-200 hover:bg-black/50"
              value={startMode}
              onChange={(e) => setStartMode(e.target.value)}
            >
              <option value="AlwaysStart">
                {t("statistics.proMatch.alwaysStart")}
              </option>
              <option value="Bulloff">
                {t("statistics.proMatch.bulloff")}
              </option>
            </select>
          </div>
        </div>

        <button
          className={`mt-8 w-full py-3 rounded-xl transition-all duration-300 font-medium text-sm ${
            isSimulating
              ? "bg-test/30 text-white/70 cursor-not-allowed"
              : "bg-test hover:bg-test/90 text-primary shadow-lg hover:shadow-test/20 active:scale-[0.98]"
          }`}
          onClick={handlePlay}
          disabled={isSimulating}
        >
          {isSimulating ? (
            <div className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {t("statistics.proMatch.simulating")}
            </div>
          ) : (
            t("statistics.proMatch.play")
          )}
        </button>
      </div>

      <div
        className={`bg-secondary/30 backdrop-blur-sm rounded-2xl p-6 sm:p-8 shadow-lg border border-primary/10 transition-all duration-300 ${
          isSimulating
            ? "opacity-50"
            : "opacity-100 hover:border-primary/20 transform hover:scale-[1.01]"
        }`}
      >
        <h4 className="text-test text-sm font-semibold mb-6 tracking-wide uppercase flex items-center">
          <span className="w-8 h-8 rounded-full bg-test/20 flex items-center justify-center mr-3">
            <svg
              className="w-4 h-4 text-test"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </span>
          {t("statistics.proMatch.resultsTitle")}
        </h4>
        {showResults && data ? (
          <div className="grid grid-cols-1 gap-5">
            <div className="bg-primary/10 p-5 rounded-xl shadow-inner hover:bg-primary/15 transition-all duration-300 transform hover:scale-[1.02]">
              <div className="text-xs text-test font-medium mb-2 tracking-wider uppercase">
                {t("statistics.proMatch.chance")}
              </div>
              <div className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                {data.match_win_rate.toFixed(2)}%
              </div>
            </div>
            <div className="bg-primary/10 p-5 rounded-xl shadow-inner hover:bg-primary/15 transition-all duration-300 transform hover:scale-[1.02]">
              <div className="text-xs text-test font-medium mb-2 tracking-wider uppercase">
                {t("statistics.proMatch.yourAvg")}
              </div>
              <div className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                {data.mean_user_avg.toFixed(2)}
              </div>
            </div>
            <div className="bg-primary/10 p-5 rounded-xl shadow-inner hover:bg-primary/15 transition-all duration-300 transform hover:scale-[1.02]">
              <div className="text-xs text-test font-medium mb-2 tracking-wider uppercase">
                {confirmedPlayer}
                <br /> {t("statistics.proMatch.opponentAvg")}
              </div>
              <div className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                {data.mean_opponent_avg.toFixed(2)}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-[250px]">
            <p className="text-white/60 text-sm italic">
              {t("statistics.proMatch.pressPlay")}
            </p>
          </div>
        )}
      </div>
      <div className="col-span-1 sm:col-span-2 mt-6 flex flex-col sm:flex-row justify-center items-center text-xs text-white/60 space-y-2 sm:space-y-0">
        {showResults && data && proMatchResults[confirmedPlayer]?.video_url ? (
          <>
            <span className="italic text-center">
              {t("statistics.proMatch.matchDataFrom")}:{" "}
              <span className="text-white/80">
                {proMatchResults[confirmedPlayer]?.tournament}
              </span>
            </span>
            <span className="hidden sm:inline mx-3">•</span>
            <a
              href={proMatchResults[confirmedPlayer]?.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-test hover:text-test/80 hover:underline font-medium transition-colors duration-200 flex items-center"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
              </svg>
              {t("statistics.proMatch.watchOnYouTube")}
            </a>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ProMatchSimulationSection;
